import { forwardRef, useCallback, useState } from 'react'
import { CircleCheckFilledIcon, ListCheckIcon, MessagesFilledIcon } from '@icons'
import { IconShape, Button } from '@platform/ui-kit'
import { m, LazyMotion, domAnimation, useAnimation } from 'framer-motion'

import Cards from './cards'
import styles from './index.module.scss'

// Animations

const OPACITY_ANIMATION = {
  start: { opacity: 0 },
  end: {
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TITLE_ANIMATION = {
  start: { opacity: 0.01, y: '50%' },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TITLE_CONTAINER_ANIMATION = {
  start: {
    y: '50%',
  },
  end: {
    y: '0%',
    transition: { type: 'spring', bounce: 0 },
  },
}

const SCROLL_INTO_VIEW = {
  start: { y: '50%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

// Const

const STEPS = [
  {
    title: 'Profile review',
    description: [
      <>
        Our team of experts will review your Olby profile to ensure it accurately reflects your
        skills and experience.
      </>,
      <>
        This will help you <span className={styles['bold']}>stand out to potential employers</span>{' '}
        and increase your chances of landing your global career.
      </>,
    ],
    icon: ListCheckIcon,
  },
  {
    title: 'Interview practices',
    description: [
      <>
        We provide you with training sessions for interviews to{' '}
        <span className={styles['bold']}>boost your chances of success</span> during an
        international selective process.
      </>,
      <>
        The practices are designed to help you prepare for small talk, answer common and specific
        questions, and ask the right questions.
      </>,
    ],
    icon: MessagesFilledIcon,
  },
]

const LIST_ITEMS = [
  'Work connected to your career preferences',
  'Stand out with a profile that reflects your experience',
  'Train for interviews',
  'Receive support each step',
]

export const LifeChangingOpportunities = forwardRef<HTMLDivElement>((_, ref) => {
  const [wasAnimationTriggered, setWasAnimationTriggered] = useState(false)

  const headingControl = useAnimation()
  const descriptionControl = useAnimation()
  const headingContainerControl = useAnimation()

  const onViewportEnter = useCallback(() => {
    if (wasAnimationTriggered) {
      return
    }
    setWasAnimationTriggered(true)

    headingControl.start('end').then(() => {
      descriptionControl.start('end')
      headingContainerControl.start('end')
    })
  }, [wasAnimationTriggered, headingControl, descriptionControl, headingContainerControl])

  return (
    <div className={styles['container']} ref={ref}>
      <LazyMotion features={domAnimation} strict>
        <m.section className={styles['content']} onViewportEnter={onViewportEnter}>
          <m.div
            variants={TITLE_CONTAINER_ANIMATION}
            initial="start"
            animate={headingContainerControl}
          >
            <m.h2
              className={styles['title']}
              initial="start"
              variants={TITLE_ANIMATION}
              animate={headingControl}
            >
              Life-changing opportunities
            </m.h2>
            <m.p
              className={styles['description']}
              animate={descriptionControl}
              variants={OPACITY_ANIMATION}
              initial="start"
            >
              We strive to find you meaningful work that allows you to make a positive impact in
              your field. When you receive an opportunity, you'll be able to review it. After
              evaluating, you should reply saying if you want to move forward or not.
            </m.p>
          </m.div>
          <Cards />
          <m.div
            className={styles['bottom-section']}
            variants={SCROLL_INTO_VIEW}
            initial="start"
            whileInView="end"
            viewport={{ once: true, amount: 0.01, margin: '-10px' }}
          >
            <div className={styles['steps-wrapper']}>
              {STEPS.map(({ title, description, icon }) => (
                <div className={styles['step']} key={title}>
                  <IconShape
                    className={styles['step-icon']}
                    icon={icon}
                    variant="on-brand"
                    size="small"
                  />
                  <h3 className={styles['step-title']}>{title}</h3>
                  <div className={styles['step-description']}>
                    {description.map(text => (
                      <p>{text}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <ul className={styles['list-card']}>
              {LIST_ITEMS.map(text => (
                <li className={styles['list-item']}>
                  <CircleCheckFilledIcon className={styles['list-icon']} />
                  {text}
                </li>
              ))}
            </ul>
            <Button size="large" href="/talent" variant="primary" className={styles['join-button']}>
              Join Olby
            </Button>
          </m.div>
        </m.section>
      </LazyMotion>
    </div>
  )
})

export default LifeChangingOpportunities
